import { useMemo, useState } from "react";
import Box from "@mui/material/Box";
import Slider from "@mui/material/Slider";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useDispatchApp, useSelectorApp } from "../../lib/redux";
import {
  commitVideoEditorClipUpdate,
  commitVideoEditorClipUpdates,
} from "../../actions/videoEditor";
import {
  selectVideoEditorPrimaryClip,
  selectVideoEditorSelectedClipIds,
  selectVideoEditorSelectedClips,
} from "./videoEditorSelectors";
import {
  clipHasCanvasLayout,
  clipHasVolume,
  getClipVolume,
} from "./clipProperties";
import {
  layoutToPercentFields,
  percentFieldsToLayout,
} from "./clipLayout";
import { useTranslationApp } from "../../lib/i18next";
import { textFieldInputSx } from "../../styles";
import {
  MODAL_VIDEO_EDITOR_PROPERTIES_EMPTY,
  MODAL_VIDEO_EDITOR_PROPERTIES_HEIGHT,
  MODAL_VIDEO_EDITOR_PROPERTIES_MIXED,
  MODAL_VIDEO_EDITOR_PROPERTIES_NAME,
  MODAL_VIDEO_EDITOR_PROPERTIES_POSITION,
  MODAL_VIDEO_EDITOR_PROPERTIES_SELECTED_COUNT,
  MODAL_VIDEO_EDITOR_PROPERTIES_SIZE,
  MODAL_VIDEO_EDITOR_PROPERTIES_TITLE,
  MODAL_VIDEO_EDITOR_PROPERTIES_VOLUME,
  MODAL_VIDEO_EDITOR_PROPERTIES_WIDTH,
  MODAL_VIDEO_EDITOR_PROPERTIES_X,
  MODAL_VIDEO_EDITOR_PROPERTIES_Y,
} from "../../i18n/keysTranslations";

const propertiesCardSx = {
  height: "100%",
  minHeight: 0,
  overflowY: "auto",
  px: 3.5,
  pt: 1.5,
  pb: 2.5,
  display: "flex",
  flexDirection: "column",
  gap: 1.5,
};

const FieldGroup = ({ title, children }) => (
  <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
    <Typography
      variant="body2"
      sx={{ fontWeight: 500, color: "text.secondary", fontSize: "13px" }}
    >
      {title}
    </Typography>
    <Box sx={{ display: "flex", gap: 1 }}>{children}</Box>
  </Box>
);

const getSharedVolume = (clips) => {
  const volumes = clips.filter(clipHasVolume).map((clip) => getClipVolume(clip));
  if (!volumes.length) {
    return null;
  }
  const first = volumes[0];
  return volumes.every((volume) => volume === first) ? first : "mixed";
};

const getSharedLayoutField = (clips, field) => {
  const values = clips
    .filter(clipHasCanvasLayout)
    .map((clip) => clip.layout)
    .filter(Boolean)
    .map((layout) => layoutToPercentFields(layout)[field]);
  if (!values.length) {
    return null;
  }
  const first = values[0];
  return values.every((value) => Math.abs(value - first) < 0.05)
    ? first
    : "mixed";
};

const LayoutField = ({ fieldKey, label, value, mixedLabel, onCommit }) => (
  <TextField
    variant="filled"
    fullWidth
    type="number"
    label={label}
    key={`${fieldKey}-${value}`}
    defaultValue={value === "mixed" ? "" : value}
    placeholder={value === "mixed" ? mixedLabel : ""}
    inputProps={{ min: 0, max: 100, step: 0.1 }}
    InputLabelProps={{ shrink: true }}
    onBlur={(event) => onCommit(event.target.value)}
    sx={textFieldInputSx}
  />
);

const VideoEditorPropertiesPanel = ({ headerAction = null }) => {
  const { t } = useTranslationApp();
  const dispatch = useDispatchApp();
  const selectedClipIds = useSelectorApp(selectVideoEditorSelectedClipIds);
  const selectedClips = useSelectorApp(selectVideoEditorSelectedClips);
  const primaryClip = useSelectorApp(selectVideoEditorPrimaryClip);
  const volumeValue = useMemo(
    () => getSharedVolume(selectedClips),
    [selectedClips]
  );
  const layoutClips = selectedClips.filter(clipHasCanvasLayout);
  const volumeClips = selectedClips.filter(clipHasVolume);
  const [draftVolume, setDraftVolume] = useState(null);
  const layoutX = useMemo(
    () => getSharedLayoutField(layoutClips, "x"),
    [layoutClips]
  );
  const layoutY = useMemo(
    () => getSharedLayoutField(layoutClips, "y"),
    [layoutClips]
  );
  const layoutWidth = useMemo(
    () => getSharedLayoutField(layoutClips, "width"),
    [layoutClips]
  );
  const layoutHeight = useMemo(
    () => getSharedLayoutField(layoutClips, "height"),
    [layoutClips]
  );

  const commitLayoutField = (field, rawValue) => {
    const nextValue = Number(rawValue);
    if (!Number.isFinite(nextValue) || !layoutClips.length) {
      return;
    }

    dispatch(
      commitVideoEditorClipUpdates({
        updates: layoutClips.map((clip) => {
          const current = layoutToPercentFields(clip.layout);
          return {
            clipId: clip.id,
            updates: {
              layout: percentFieldsToLayout({
                ...current,
                [field]: nextValue,
                currentLayout: clip.layout,
              }),
            },
          };
        }),
      })
    );
  };

  const title =
    selectedClipIds.length > 1
      ? t(MODAL_VIDEO_EDITOR_PROPERTIES_SELECTED_COUNT, {
          count: selectedClipIds.length,
        })
      : t(MODAL_VIDEO_EDITOR_PROPERTIES_TITLE);

  if (!selectedClipIds.length) {
    return (
      <Box sx={propertiesCardSx}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            minHeight: 36,
          }}
        >
          {headerAction}
          <Typography sx={{ fontWeight: 600 }}>{title}</Typography>
        </Box>
        <Typography variant="body2" color="text.secondary">
          {t(MODAL_VIDEO_EDITOR_PROPERTIES_EMPTY)}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={propertiesCardSx}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          minHeight: 36,
        }}
      >
        {headerAction}
        <Typography sx={{ fontWeight: 600 }}>{title}</Typography>
      </Box>
      {selectedClipIds.length === 1 && primaryClip ? (
        <TextField
          variant="filled"
          fullWidth
          label={t(MODAL_VIDEO_EDITOR_PROPERTIES_NAME)}
          defaultValue={primaryClip.label || ""}
          key={primaryClip.id}
          InputLabelProps={{ shrink: true }}
          onBlur={(event) => {
            const nextLabel = event.target.value.trim();
            if (nextLabel && nextLabel !== primaryClip.label) {
              dispatch(
                commitVideoEditorClipUpdate({
                  clipId: primaryClip.id,
                  updates: { label: nextLabel },
                })
              );
            }
          }}
          sx={textFieldInputSx}
        />
      ) : null}
      {layoutClips.length ? (
        <>
          <FieldGroup title={t(MODAL_VIDEO_EDITOR_PROPERTIES_POSITION)}>
            <LayoutField
              fieldKey="x"
              label={t(MODAL_VIDEO_EDITOR_PROPERTIES_X)}
              value={layoutX}
              mixedLabel={t(MODAL_VIDEO_EDITOR_PROPERTIES_MIXED)}
              onCommit={(value) => commitLayoutField("x", value)}
            />
            <LayoutField
              fieldKey="y"
              label={t(MODAL_VIDEO_EDITOR_PROPERTIES_Y)}
              value={layoutY}
              mixedLabel={t(MODAL_VIDEO_EDITOR_PROPERTIES_MIXED)}
              onCommit={(value) => commitLayoutField("y", value)}
            />
          </FieldGroup>
          <FieldGroup title={t(MODAL_VIDEO_EDITOR_PROPERTIES_SIZE)}>
            <LayoutField
              fieldKey="width"
              label={t(MODAL_VIDEO_EDITOR_PROPERTIES_WIDTH)}
              value={layoutWidth}
              mixedLabel={t(MODAL_VIDEO_EDITOR_PROPERTIES_MIXED)}
              onCommit={(value) => commitLayoutField("width", value)}
            />
            <LayoutField
              fieldKey="height"
              label={t(MODAL_VIDEO_EDITOR_PROPERTIES_HEIGHT)}
              value={layoutHeight}
              mixedLabel={t(MODAL_VIDEO_EDITOR_PROPERTIES_MIXED)}
              onCommit={(value) => commitLayoutField("height", value)}
            />
          </FieldGroup>
        </>
      ) : null}
      {volumeClips.length ? (
        <Box>
          <Typography
            variant="body2"
            sx={{ fontWeight: 500, color: "text.secondary", fontSize: "13px", mb: 0.5 }}
          >
            {volumeValue === "mixed"
              ? `${t(MODAL_VIDEO_EDITOR_PROPERTIES_VOLUME)} · ${t(
                  MODAL_VIDEO_EDITOR_PROPERTIES_MIXED
                )}`
              : t(MODAL_VIDEO_EDITOR_PROPERTIES_VOLUME)}
          </Typography>
          <Slider
            size="small"
            color="primary"
            min={0}
            max={100}
            value={
              draftVolume ??
              (volumeValue === "mixed"
                ? 100
                : Math.round((volumeValue || 0) * 100))
            }
            valueLabelDisplay="auto"
            onChange={(_event, value) => {
              setDraftVolume(Array.isArray(value) ? value[0] : value);
            }}
            onChangeCommitted={(_event, value) => {
              const volume = (Array.isArray(value) ? value[0] : value) / 100;
              setDraftVolume(null);
              dispatch(
                commitVideoEditorClipUpdates({
                  updates: volumeClips.map((clip) => ({
                    clipId: clip.id,
                    updates: { volume },
                  })),
                })
              );
            }}
          />
        </Box>
      ) : null}
    </Box>
  );
};

export default VideoEditorPropertiesPanel;
